"use client";

import { isEditing } from "@builder.io/sdk-react";

import { QueryClient, QueryClientProvider } from "@jti/rest-api";
import { ConsentReminderDialogContainer } from "@jti/ui/components/ConsentReminderDialog";

import { useDisplayModelComponent } from "@/libs/builder/hooks/useDisplayModelComponent";

import type { consentReminderDialogInputs } from "./ConsentReminderDialog.builder";
import { useConsentReminderMode } from "./ConsentReminderModeContext";

export type ConsentReminderDialogProps = BuilderComponentProps<typeof consentReminderDialogInputs>;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

export const ConsentReminderDialog = ({
  title,
  subtitle,
  description,
  isMarketingConsentsConfirmationEnabled,
  desktopImage,
  mobileImage,
  altImageText,
  isClosable,
  offerConsentName,
  productInformationConsentName,
  ploomClubConsentName,
  consentsLegalText,
  consentOptionsListType,
  confirmChoiceText,
  primaryButtonHref,
  secondaryButtonText,
  secondaryButtonHref,
  maxOccurrencesPerYear = 0,
  consentExpirationLimit = 0,
  popUpDelay = 0,
}: ConsentReminderDialogProps) => {
  const shouldDisplayModelComponent = useDisplayModelComponent("consent-reminder-dialog");
  const consentReminderMode = useConsentReminderMode();

  const isGlobalReminderEnabled = consentReminderMode === "global";
  const isBuilderEditing = isEditing();

  if (!shouldDisplayModelComponent || (!isBuilderEditing && !isGlobalReminderEnabled)) {
    return null;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <ConsentReminderDialogContainer
        dataTestId="global-consent-reminder-dialog"
        isEnabled={isGlobalReminderEnabled}
        forceOpened={isBuilderEditing}
        maximumOccurrencesPerYear={maxOccurrencesPerYear}
        consentExpirationLimit={consentExpirationLimit}
        popUpDelay={popUpDelay}
        offerConsentName={offerConsentName}
        productInformationConsentName={productInformationConsentName}
        ploomClubConsentName={ploomClubConsentName}
        consentsLegalText={consentsLegalText}
        consentOptionsListType={consentOptionsListType}
        mainImage={desktopImage}
        desktopImage={desktopImage}
        mobileImage={mobileImage}
        imageAltText={altImageText}
        title={title}
        subtitle={subtitle}
        description={description}
        primaryButtonText={confirmChoiceText}
        primaryButtonHref={primaryButtonHref}
        secondaryButtonText={secondaryButtonText}
        secondaryButtonHref={secondaryButtonHref}
        closable={isClosable}
        isMarketingConsentsConfirmationEnabled={isMarketingConsentsConfirmationEnabled}
      />
    </QueryClientProvider>
  );
};
